import { useState } from 'react';
import {
  Modal, Stack, NumberInput, TextInput, Select, Button, Group, Alert,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { api } from '../../apiClient';

const VENDOR_OPTIONS = [
  { value: 'aws',       label: 'AWS' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'google',    label: 'Google' },
];

function AddCertificationModal({ opened, onClose, onSuccess }) {
  const [id, setId]                   = useState('');
  const [code, setCode]               = useState('');
  const [name, setName]               = useState('');
  const [formalName, setFormalName]   = useState('');
  const [vendor, setVendor]           = useState(null);
  const [examGuideUrl, setExamGuideUrl] = useState('');
  const [urlKeyword, setUrlKeyword]     = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);

  const handleClose = () => {
    setId('');
    setCode('');
    setName('');
    setFormalName('');
    setVendor(null);
    setExamGuideUrl('');
    setUrlKeyword('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);

    // フロントエンドバリデーション
    const idNum = parseInt(id, 10);
    if (!id || isNaN(idNum) || idNum < 100 || idNum > 999) {
      setError('資格 ID は 100〜999 の整数で入力してください。');
      return;
    }
    if (!code.trim()) {
      setError('コードは必須です。');
      return;
    }
    if (!name.trim()) {
      setError('名称は必須です。');
      return;
    }
    if (!vendor) {
      setError('ベンダーを選択してください。');
      return;
    }

    setLoading(true);
    try {
      await api.post('/certifications', {
        id: idNum,
        code: code.trim(),
        name: name.trim(),
        formal_name: formalName.trim() || null,
        vendor,
        exam_guide_url: examGuideUrl.trim() || null,
        url_keyword: urlKeyword.trim() || null,
      });
      handleClose();
      onSuccess();
    } catch (e) {
      let msg = '登録に失敗しました。';
      try {
        const parsed = JSON.parse(e.message);
        if (parsed.error) msg = parsed.error;
      } catch {
        if (e.message) msg = e.message;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="資格を追加" size="md">
      <Stack gap="sm">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {error}
          </Alert>
        )}

        <NumberInput
          label="資格 ID"
          description="100〜999 の整数（例: 201）"
          placeholder="201"
          min={100}
          max={999}
          allowDecimal={false}
          value={id}
          onChange={setId}
          required
        />

        <TextInput
          label="コード"
          description="小文字のコード（例: saac03）"
          placeholder="saac03"
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
          required
        />

        <TextInput
          label="名称"
          description="略称（例: SAA-C03）"
          placeholder="SAA-C03"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
        />

        <TextInput
          label="正式名称"
          description="formal_name（例: Solutions Architect - Associate）"
          placeholder="Solutions Architect - Associate"
          value={formalName}
          onChange={(e) => setFormalName(e.currentTarget.value)}
        />

        <Select
          label="ベンダー"
          placeholder="ベンダーを選択"
          data={VENDOR_OPTIONS}
          value={vendor}
          onChange={setVendor}
          required
        />

        <TextInput
          label="試験ガイド URL"
          placeholder="https://..."
          value={examGuideUrl}
          onChange={(e) => setExamGuideUrl(e.currentTarget.value)}
        />

        <TextInput
          label="ExamTopics URL キーワード"
          description="スクレイパーが URL から資格を判別するキーワード（例: saa-c03, professional-aip）"
          placeholder="saa-c03"
          value={urlKeyword}
          onChange={(e) => setUrlKeyword(e.currentTarget.value)}
        />

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={handleClose} disabled={loading}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            登録
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default AddCertificationModal;
